import {
  OrganizationPermissionFlag,
  UserLimits,
  WorkspacePermissionFlag,
} from '@robocorp/robocloud-internal-cds-sdk';
import type {
  OrganizationRestriction,
  UserRestriction,
  UserRoleRestriction,
  WorkspaceRestriction,
} from './types';

/**
 * Returns true if passes user role check
 */
export const passesUserRole = ({
  restriction,
  isRealmAdmin,
  isSSO,
}: {
  restriction: UserRoleRestriction;
  isRealmAdmin?: boolean;
  isSSO?: boolean;
}) => {
  if (restriction === 'base' && !isSSO) return true;
  if (restriction === 'sso' && isSSO) return true;
  if (restriction === 'ssoRealmAdmin' && isSSO && isRealmAdmin) return true;

  return false;
};

/**
 * Returns true if passes user restriction check
 */
export const passesUserRestrictions = ({
  restrictions,
  permissions,
}: {
  restrictions: readonly UserRestriction[];
  permissions?: UserLimits;
}) =>
  permissions ? restrictions.every((restriction) => permissions[restriction] === true) : false;

/**
 * Returns true if passes organization subscription check
 */
export const passesSubscription = ({
  restrictions,
  mode,
  subscriptionId,
}: {
  restrictions: readonly string[];
  mode: 'whitelist' | 'blacklist';
  subscriptionId?: string;
}) => {
  const included = subscriptionId !== undefined && restrictions.includes(subscriptionId);
  return mode === 'blacklist' ? !included : included;
};

/**
 * Returns true if passes organization role check
 */
export const passesOrganizationRole = ({
  restriction,
  roleId,
}: {
  restriction: string;
  roleId?: string;
}) => restriction === roleId;

/**
 * Returns true if passes organization restriction check
 */
export const passesOrganizationRestrictions = ({
  restrictions,
  permissions,
}: {
  restrictions: readonly OrganizationRestriction[];
  permissions?: readonly OrganizationPermissionFlag[];
}) =>
  permissions ? restrictions.every((restriction) => permissions.includes(restriction)) : false;

/**
 * Returns true if passes workspace role check
 */
export const passesWorkspaceRole = ({
  restriction,
  roleId,
}: {
  restriction: string;
  roleId?: string;
}) => restriction === roleId;

/**
 * Returns true if passes workspace restriction check
 */
export const passesWorkspaceRestrictions = ({
  restrictions,
  permissions,
}: {
  restrictions: readonly WorkspaceRestriction[];
  permissions?: readonly WorkspacePermissionFlag[];
}) =>
  permissions
    ? restrictions.every((restriction) =>
        permissions.includes(restriction as WorkspacePermissionFlag),
      )
    : false;
